/**
 * WooCommerce Auto Messages — frontend-only catalog.
 *
 * Mirrors `backend/src/domain/constants/shopifyPlaybooks.js` but scoped to
 * WooCommerce events, and lives on the frontend because the backend half
 * of this feature (webhook intake, catalog service, routes, flow graph
 * generation) hasn't been wired yet. The UI renders these as cards; the
 * Enable button shows a "Coming soon" toast until the backend is in place.
 *
 * When the backend ships, move this file to
 *   backend/src/domain/constants/wooCommerceAutoMessages.js
 * and expose a `GET /api/v1/woocommerce/auto-messages/catalog` endpoint
 * that returns it. The UI should then read from there so adding a new
 * event on the backend automatically shows up on the frontend.
 *
 * Event names follow the same convention as the Shopify catalog — the
 * `triggerType` value is what the TriggerDispatcher already supports for
 * WooCommerce (dispatched by
 * `WooCommerceConnectorService.dispatchWooCommerceOrderTriggers`).
 */

export type WooCommerceAutoMessageKey =
  | 'order_placed'
  | 'order_processing'
  | 'order_completed'
  | 'order_cancelled'
  | 'order_refunded'
  | 'order_on_hold'
  | 'abandoned_cart'
  | 'new_customer';

export interface WooCommerceAutoMessageCatalogEntry {
  key: WooCommerceAutoMessageKey;
  title: string;
  description: string;
  /** Icon key — same lookup table as Shopify uses in `playbookIcons.ts`. */
  icon: string;
  /** DB-side trigger type the dispatcher matches on. */
  triggerType: string;
}

export const WOOCOMMERCE_AUTO_MESSAGES: WooCommerceAutoMessageCatalogEntry[] = [
  {
    key: 'order_placed',
    title: 'Order Placed',
    description: 'Sent as soon as a new WooCommerce order is created.',
    icon: 'shopping-bag',
    triggerType: 'order.placed',
  },
  {
    key: 'order_processing',
    title: 'Order Processing',
    description: 'Sent when payment is received and the order moves to processing.',
    icon: 'credit-card',
    triggerType: 'order.paid',
  },
  {
    key: 'order_completed',
    title: 'Order Completed',
    description: 'Sent when the order is marked complete / fulfilled.',
    icon: 'check-circle',
    triggerType: 'order.delivered',
  },
  {
    key: 'order_cancelled',
    title: 'Order Cancelled',
    description: 'Sent when an order is cancelled.',
    icon: 'x-circle',
    triggerType: 'order.cancelled',
  },
  {
    key: 'order_refunded',
    title: 'Refund Issued',
    description: 'Sent when a refund is issued against an order.',
    icon: 'rotate-ccw',
    triggerType: 'refund.created',
  },
  {
    key: 'order_on_hold',
    title: 'Order On Hold',
    description: 'Sent when an order is placed on hold (e.g. awaiting payment).',
    icon: 'shopping-cart',
    triggerType: 'checkout.updated',
  },
  {
    key: 'abandoned_cart',
    title: 'Abandoned Cart',
    description: 'Sent when a customer leaves items in their cart without checking out.',
    icon: 'shopping-cart',
    triggerType: 'checkout.updated',
  },
  {
    key: 'new_customer',
    title: 'New Customer',
    description: 'Sent when a new customer signs up on your WooCommerce store.',
    icon: 'user-plus',
    triggerType: 'customer.created',
  },
];
